// src/components/admin/ProductFormModal/ProductFormModal.jsx
import React from 'react';
import ImageUpload from '../../../components/common/ImageUpload/ImageUpload';
import { UPLOAD_FOLDERS } from '../../../services/cloudinary/cloudinary.service';
import VariantManager from './VariantManager';
import './ProductFormModal.css';

const ProductFormModal = ({
  show,
  mode,
  formData,
  categories,
  colors,
  materials,
  materialsByCategory,
  loading,
  featureInput,
  variantForm,
  onClose,
  onSubmit,
  onInputChange,
  onImageUpload,
  onRemoveImage,
  onSetPrimaryImage,
  onFeatureInputChange,
  onAddFeature,
  onRemoveFeature,
  onShowCategoryModal,
  onShowColorModal,
  onShowMaterialModal,
  onVariantTypeChange,
  onVariantFormChange,
  onAddVariant,
  onRemoveVariant
}) => {
  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="product-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{mode === 'add' ? 'Ajouter un produit' : 'Modifier le produit'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={onSubmit} className="modal-form">
          <div className="form-grid">
            {/* Images Section */}
            <div className="form-group full-width">
              <label>Images du produit * (La première image sera l'image principale)</label>

              {formData.images.length > 0 && (
                <div className="uploaded-images-grid">
                  {formData.images.map((img, index) => (
                    <div key={index} className="uploaded-image-item">
                      <img src={img.url} alt={`Product ${index + 1}`} />
                      <div className="image-item-overlay">
                        {index === 0 && (
                          <span className="primary-badge">Principale</span>
                        )}
                        <div className="image-item-actions">
                          {index !== 0 && (
                            <button
                              type="button"
                              className="btn-icon btn-primary-small"
                              onClick={() => onSetPrimaryImage(index)}
                              title="Définir comme principale"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                              </svg>
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-icon btn-delete-small"
                            onClick={() => onRemoveImage(index)}
                            title="Supprimer"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: formData.images.length > 0 ? '16px' : '0' }}>
                <ImageUpload
                  onUploadComplete={onImageUpload}
                  folder={UPLOAD_FOLDERS.PRODUCTS}
                  maxSize={6}
                  multiple={true}
                  buttonText={formData.images.length > 0 ? 'Ajouter une autre image' : 'Télécharger la première image'}
                />
              </div>

              <p className="form-hint">
                {formData.images.length === 0
                  ? 'Téléchargez au moins une image pour le produit'
                  : `${formData.images.length} image(s) téléchargée(s). Vous pouvez en ajouter d'autres.`
                }
              </p>
            </div>

            {/* Basic Info */}
            <div className="form-group">
              <label>Nom du produit *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={onInputChange}
                required
                placeholder="Ex: Bureau ergonomique"
              />
            </div>

            <div className="form-group">
              <label>Catégorie *</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  name="category"
                  value={formData.category}
                  onChange={onInputChange}
                  required
                  style={{ flex: 1 }}
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-icon btn-edit"
                  onClick={onShowCategoryModal}
                  title="Ajouter une catégorie"
                  style={{ flexShrink: 0 }}
                >
                  +
                </button>
              </div>
            </div>

            <div className="form-group full-width">
              <label>Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={onInputChange}
                required
                rows="3"
                placeholder="Description détaillée du produit"
              />
            </div>

            <div className="form-group">
              <label>Marque</label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={onInputChange}
                placeholder="Ex: BuroTime"
              />
            </div>

            <div className="form-group">
              <label>Badge</label>
              <select name="badge" value={formData.badge} onChange={onInputChange}>
                <option value="">Aucun</option>
                <option value="Nouveau">Nouveau</option>
                <option value="Promo">Promo</option>
                <option value="Hot">Hot</option>
                <option value="Bestseller">Bestseller</option>
              </select>
            </div>

            {/* Variant Toggle */}
            <div className="form-group full-width variant-toggle">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.hasVariants}
                  onChange={(e) => {
                    onInputChange({ 
                      target: { 
                        name: 'hasVariants', 
                        type: 'checkbox', 
                        checked: e.target.checked 
                      } 
                    });
                    if (!e.target.checked) {
                      onInputChange({ 
                        target: { 
                          name: 'variants', 
                          value: [] 
                        } 
                      });
                    }
                  }}
                />
                <span>Ce produit a plusieurs variantes (dimensions, couleurs, matériaux)</span>
              </label>
              <p className="form-hint" style={{ marginTop: '8px' }}>
                Activez cette option si votre produit existe en plusieurs tailles, couleurs ou matériaux avec des prix différents
              </p>
            </div>
          </div>

          {/* Variant Manager Component */}
          {formData.hasVariants && (
            <VariantManager
              formData={formData}
              variantForm={variantForm}
              colors={colors}
              materials={materials}
              materialsByCategory={materialsByCategory}
              onVariantTypeChange={onVariantTypeChange}
              onVariantFormChange={onVariantFormChange}
              onAddVariant={onAddVariant}
              onRemoveVariant={onRemoveVariant}
              onShowColorModal={onShowColorModal}
              onShowMaterialModal={onShowMaterialModal}
            />
          )}

          {/* Standard Fields (for non-variant products) */}
          {!formData.hasVariants && (
            <div className="form-grid">
              <div className="form-group">
                <label>Prix (DA) *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={onInputChange}
                  required
                  min="0"
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label>Ancien prix (DA)</label>
                <input
                  type="number"
                  name="oldPrice"
                  value={formData.oldPrice}
                  onChange={onInputChange}
                  min="0"
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label>Matériau *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    name="material"
                    value={formData.material}
                    onChange={onInputChange}
                    required
                    style={{ flex: 1 }}
                  >
                    <option value="">Sélectionner un matériau</option>
                    {Object.entries(materialsByCategory).map(([category, mats]) => (
                      <optgroup key={category} label={category}>
                        {mats.map(material => (
                          <option key={material.id} value={material.value}>
                            {material.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn-icon btn-edit"
                    onClick={onShowMaterialModal}
                    title="Ajouter matériau"
                  >
                    <strong>+</strong>
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Couleur *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    name="color"
                    value={formData.color}
                    onChange={onInputChange}
                    required
                    style={{ flex: 1 }}
                  >
                    <option value="">Sélectionner une couleur</option>
                    {colors.map(color => (
                      <option key={color.id} value={color.value}>{color.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn-icon btn-edit"
                    onClick={onShowColorModal}
                    title="Ajouter couleur"
                  >
                    <strong>+</strong>
                  </button>
                </div>
                {formData.color && (
                  <div style={{ 
                    marginTop: '8px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px' 
                  }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      border: '2px solid #e2e8f0',
                      backgroundColor: colors.find(c => c.value === formData.color)?.hex
                    }} />
                    <span style={{ fontSize: '13px', color: '#64748b' }}>
                      Aperçu de la couleur sélectionnée
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stock & Rating */}
          <div className="form-grid">
            <div className="form-group">
              <label>Quantité en stock *</label>
              <input
                type="number"
                name="stockCount"
                value={formData.stockCount}
                onChange={onInputChange}
                min="0"
                placeholder="0"
                required
              />
              <p className="form-hint" style={{ marginTop: '8px' }}>
                Statut détecté: <strong>
                  {formData.stockCount === 0 || formData.stockCount === '' 
                    ? 'Rupture de stock' 
                    : Number(formData.stockCount) <= 10
                      ? `Stock faible (${formData.stockCount} unités)`
                      : `En stock (${formData.stockCount} unités)`}
                </strong>
              </p>
            </div>

            <div className="form-group">
              <label>Statut du stock (Auto-détecté)</label>
              <div className="stock-status-display" style={{
                padding: '12px 16px',
                borderRadius: '10px',
                backgroundColor: formData.stock === 'in-stock' ? '#d1fae5' 
                               : formData.stock === 'low-stock' ? '#fed7aa'
                               : '#fecaca',
                color: formData.stock === 'in-stock' ? '#065f46' 
                     : formData.stock === 'low-stock' ? '#92400e'
                     : '#991b1b',
                fontWeight: '600',
                fontSize: '15px'
              }}>
                {formData.stock === 'in-stock' && '✓ En stock'}
                {formData.stock === 'low-stock' && '⚠ Stock faible'}
                {formData.stock === 'out-of-stock' && '✕ Rupture de stock'}
              </div>
            </div>

            <div className="form-group">
              <label>Note (0-5)</label>
              <input
                type="number"
                name="rating"
                value={formData.rating}
                onChange={onInputChange}
                min="0"
                max="5"
                step="0.1"
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label>Nombre d'avis</label>
              <input
                type="number"
                name="reviews"
                value={formData.reviews}
                onChange={onInputChange}
                min="0"
                placeholder="0"
              />
            </div>
          </div>

          {/* Features */}
          <div className="form-group full-width">
            <label>Caractéristiques</label>
            <div className="features-input">
              <input
                type="text"
                value={featureInput}
                onChange={(e) => onFeatureInputChange(e.target.value)}
                placeholder="Ajouter une caractéristique"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAddFeature())}
              />
              <button type="button" onClick={onAddFeature} className="btn-add-feature">
                Ajouter
              </button>
            </div>
            {formData.features.length > 0 && (
              <div className="features-list">
                {formData.features.map((feature, index) => (
                  <div key={index} className="feature-tag">
                    <span>{feature}</span>
                    <button type="button" onClick={() => onRemoveFeature(index)}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Enregistrement...' : mode === 'add' ? 'Ajouter' : 'Modifier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductFormModal;